import Image from "next/image";
import React, { useEffect, useRef, useState } from "react";
// import styles from "./client.module.scss";
import { toWebp } from "@/utils/common";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Splide, SplideSlide } from "@splidejs/react-splide";
import { AutoScroll } from "@splidejs/splide-extension-auto-scroll";
import "@splidejs/react-splide/css";
const baseUrl = process.env.NEXT_PUBLIC_REACT_APP_RESOURCE_BASE_URL || "";
interface CardProps {
  item: any;
  className?: string;

}

const Card: React.FC<CardProps> = ({ item,
  className,
 }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showCountdown, setShowCountdown] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [progress, setProgress] = useState(0);

  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const progressIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const countdownDuration = 3;

  const router = useRouter();

  const handleMouseEnter = () => {
    setIsHovered(true);

    // Clear any existing timeouts
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Start the hover delay timer
    hoverTimeoutRef.current = setTimeout(() => {
      setShowCountdown(true);
      setCountdown(countdownDuration);
      setProgress(0);
      startCountdown();
    }, 3000);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setShowCountdown(false);
    setCountdown(countdownDuration);
    setProgress(0);

    // Clear all timers
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }
    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
    }
  };

  const startCountdown = () => {
    let timeLeft = countdownDuration;

    // Update countdown every 100ms for smooth progress
    countdownIntervalRef.current = setInterval(() => {
      timeLeft -= 0.1;

      if (timeLeft <= 0) {
        // Redirect when countdown reaches 0
        // if (item?.url) window.location.href = item.url;
        if (item.attributes?.slug)
          router.push(`/case-studies/${item.attributes.slug}`);
        return;
      }

      setCountdown(Math.ceil(timeLeft));
    }, 100);

    // Update progress bar
    const totalSteps = countdownDuration * 10; // 10 steps per second
    let currentStep = 0;

    progressIntervalRef.current = setInterval(() => {
      currentStep++;
      const progressPercent = (currentStep / totalSteps) * 100;
      setProgress(progressPercent);

      if (currentStep >= totalSteps) {
        clearInterval(progressIntervalRef.current!);
      }
    }, 100);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (countdownIntervalRef.current)
        clearInterval(countdownIntervalRef.current);
      if (progressIntervalRef.current)
        clearInterval(progressIntervalRef.current);
    };
  }, []);

  // Calculate circular progress
  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  return (
    <div className={`${className} relative border-t border-border pt-[32px]`}>
      <Link
        href={`/case-studies/${item?.attributes?.slug}`}
        className="flex flex-col md:justify-between justify-start md:h-full h-auto"
      >
        <div>
          <h3 className="text-heading-4 font-bold text-primary-text">
            {item?.attributes?.title}
          </h3>
          <div className="flex xl:flex-row flex-col md:gap-[12px_32px] gap-[12px] mt-4">
            <p className="flex gap-2 text-subtitle-3 font-semibold text-primary-text">
              <span>Resources Utilized:</span>
              {item?.attributes?.resources_utilized || "5-6"}
            </p>
            <p className="flex gap-2 text-subtitle-3 font-semibold text-primary-text">
              <span>Engagement Period:</span>
              {item?.attributes?.engagement_period || "7-8 Months"}
            </p>
          </div>
          {/* Hover indicator */}
          <div
            className={`
              absolute bottom-4 right-4 text-xs text-gray-400 transition-opacity duration-200
              ${isHovered && !showCountdown ? "opacity-100" : "opacity-0"}
            `}
          >
            Keep hovering...
          </div>
        </div>
        <div
          className="relative pt-[32px]"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <Image
            src={toWebp(
              `${baseUrl}${item?.attributes?.image?.data?.attributes?.url}`
            )}
            alt={item?.attributes?.image?.data?.attributes?.alt}
            width={970 * 2}
            height={718 * 2}
            quality={100}
            sizes="100vw"
            className="w-full h-[360px] block object-cover"
            loading="lazy"
          />

          <div className="absolute w-full left-0 bottom-0 px-[10px] pt-[20px] md:p-[20px_25px] bg-[linear-gradient(180deg,rgba(176,217,247,0.3)_0%,#0086E680_100%)] backdrop-blur-[10px] transition-opacity duration-300 overflow-hidden">
            <div className="flex justify-between items-center md:flex-row flex-col gap-[20px]">
              <p className="text-white font-[600] text-[16px] leading-[22.2px]">
                Tech Stack
              </p>

              {item?.attributes?.technologies_new?.length > 3 ? (
                <div
                  className={`mb-[10px] sm:mb-[0px] w-[100%] sm:w-[40%] xl:w-[30%]`}
                >
                  <Splide
                    options={{
                      type: "loop",
                      drag: "free",
                      focus: "center",
                      arrows: false,
                      pagination: false,
                      autoWidth: true,
                      gap: "8px",
                      autoScroll: {
                        speed: 1, // try values between 0.5 - 2 for smoothness
                        pauseOnHover: true,
                        pauseOnFocus: false,
                      },
                    }}
                    extensions={{ AutoScroll }}
                    className="marquee-splide"
                  >
                    {item?.attributes?.technologies_new?.map(
                      (tech: any, idx: number) => (
                        <SplideSlide key={`stack-${idx}`}>
                          <div className="bg-white flex justify-center items-center w-[42px] h-[42px]">
                            {tech.attributes?.image?.data?.attributes?.url ? (
                              <Image
                                width={42}
                                height={42}
                                src={
                                  tech.attributes?.image?.data?.attributes?.url
                                    ? `${baseUrl}${toWebp(tech.attributes.image.data.attributes.url)}`
                                    : "/fallback-tech.svg"
                                }
                                alt={
                                  tech.attributes?.image?.data?.attributes
                                    ?.alternativeText || tech.attributes?.name
                                }
                                className="w-[32px] h-[32px] object-cover"
                                quality={100}
                              />
                            ) : (
                              <div className="w-[32px] h-[32px] bg-[#E6EAF0]" />
                            )}
                          </div>
                        </SplideSlide>
                      )
                    )}
                  </Splide>
                </div>
              ) : (
                <div className="mb-[10px] sm:mb-[0px] w-[100%] sm:w-[50%] xl:w-[30%]  flex gap-[12px] whitespace-nowrap justify-end items-center overflow-hidden">
                  <div className="flex gap-[12px]">
                    {item?.attributes?.technologies_new?.map(
                      (tech: any, i: number) => {
                        const technology = tech.attributes;

                        return (
                          <div
                            key={"stack-0-" + i}
                            className="bg-white w-[42px] h-[42px] aspect-square flex items-center justify-center object-contain object-center p-[4px] flex-shrink-0"
                          >
                            <Image
                              src={
                                technology?.image?.data?.attributes?.url
                                  ? `${baseUrl}${toWebp(technology.image.data.attributes.url)}`
                                  : "/fallback-tech.svg"
                              }
                              alt={
                                technology?.image?.data?.attributes
                                  ?.alternativeText || technology?.name
                              }
                              width={100}
                              height={100}
                              quality={100}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          </div>
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Countdown Overlay */}
          {showCountdown && (
            <div className="absolute inset-0 flex items-center justify-center z-50">
              <div className="text-center">
                {/* Circular Progress Bar */}
                <div className="relative w-24 h-24 flex justify-center items-center mx-auto">
                  <svg
                    className="w-24 h-24 transform -rotate-90"
                    viewBox="0 0 100 100"
                  >
                    {/* Background circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r={radius}
                      stroke="transparent"
                      strokeWidth="4"
                      fill="none"
                    />
                    {/* Progress circle */}
                    <circle
                      cx="50"
                      cy="50"
                      r={radius}
                      stroke="white"
                      strokeWidth="4"
                      fill="none"
                      strokeDasharray={circumference}
                      strokeDashoffset={strokeDashoffset}
                      strokeLinecap="round"
                      className="transition-all duration-100 ease-linear"
                    />
                  </svg>

                  {/* Countdown number */}
                  <div className="absolute inset-0 flex items-center bg-[#0404054D] justify-center aspect-square w-16 h-16 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 border border-white rounded-full">
                    <span className="text-2xl font-bold text-white">
                      {countdown}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
};
export default Card;